export type FreemiumTier = 'diy' | 'licensedAssist' | 'licensedFullService';

export interface FreemiumTierConfig {
  tier: FreemiumTier;
  name: string;
  priceUsd: number;
  description: string;
  ctaLabel: string;
  features: string[];
  /** When true, the tier is visually featured (primary background) on the paywall. */
  recommended?: boolean;
  /**
   * Licensed tiers hand off to Wealth Assurance Group (WAG) rather than unlocking
   * self-serve planning. They show the WAG disclosure and trigger the handoff email.
   */
  isLicensed: boolean;
  /**
   * Pre-created Stripe Price IDs per environment. Replace the placeholders with real
   * Stripe Price objects before enabling checkout in that environment.
   */
  stripePriceIds: {
    dev: string;
    prod: string;
  };
}

export const FREEMIUM_TIER_CONFIGS: Record<FreemiumTier, FreemiumTierConfig> = {
  diy: {
    tier: 'diy',
    name: 'Do it Yourself',
    priceUsd: 50,
    description:
      'Build a plan yourself with clear, step-by-step tools. Unlock all advanced planning features for {clientName}.',
    ctaLabel: 'Get started',
    features: [
      'Unlock all advanced planning tools',
      'Plan at your own pace',
      'Clear step-by-step guidance',
    ],
    isLicensed: false,
    stripePriceIds: {
      dev: 'price_1TeE0TCrvzKtEHNdt0XvL7PT',
      prod: 'price_FREEMIUM_DIY_PROD_PLACEHOLDER',
    },
  },
  licensedAssist: {
    tier: 'licensedAssist',
    name: 'Licensed Assist',
    priceUsd: 499,
    description:
      'On-demand licensed help. Our team is here to support your planning goals. Hand the baton off at any point.',
    ctaLabel: 'Get licensed help',
    features: [
      'On-demand help from a licensed expert',
      'Hand off any step to our team',
      'Stay in control of your plan',
    ],
    recommended: true,
    isLicensed: true,
    stripePriceIds: {
      dev: 'price_1TeE1HCrvzKtEHNd8PRLvCP4',
      prod: 'price_FREEMIUM_LICENSED_ASSIST_PROD_PLACEHOLDER',
    },
  },
  licensedFullService: {
    tier: 'licensedFullService',
    name: 'Licensed Full Service',
    priceUsd: 499,
    description:
      'A dedicated expert builds your plan start to finish, fit to your goals. We follow your guidelines around insurance, funding tradeoffs, and planning preferences.',
    ctaLabel: 'Get full service',
    features: [
      'A dedicated expert builds your plan',
      'Tailored to your goals and guidelines',
      'Done for you, start to finish',
    ],
    isLicensed: true,
    stripePriceIds: {
      dev: 'price_1TeE1HCrvzKtEHNd8PRLvCP4',
      prod: 'price_FREEMIUM_LICENSED_FULL_SERVICE_PROD_PLACEHOLDER',
    },
  },
};

/** Tiers in display order for the paywall. */
export const FREEMIUM_TIERS_IN_ORDER: FreemiumTierConfig[] = [
  FREEMIUM_TIER_CONFIGS.diy,
  FREEMIUM_TIER_CONFIGS.licensedAssist,
  FREEMIUM_TIER_CONFIGS.licensedFullService,
];

export function getFreemiumTierConfig(tier: FreemiumTier): FreemiumTierConfig {
  return FREEMIUM_TIER_CONFIGS[tier];
}

/** Resolves the Stripe Price ID to charge for a tier in the given environment. */
export function getFreemiumStripePriceId(tier: FreemiumTier, isProd: boolean): string {
  const config = FREEMIUM_TIER_CONFIGS[tier];

  return isProd ? config.stripePriceIds.prod : config.stripePriceIds.dev;
}

export function isFreemiumTier(value: string): value is FreemiumTier {
  return value === 'diy' || value === 'licensedAssist' || value === 'licensedFullService';
}

// The "What are you trying to do next for {clientName}?" intent options. The selected
// id is recorded with each paywall hit for the experiment metrics.
export const FREEMIUM_INTENT_OPTIONS = [
  { id: 'rate-action', label: 'Navigate a policy rate action' },
  { id: 'self-funding', label: 'Evaluate self-funding' },
  { id: 'analyze-policy', label: 'Analyze a policy' },
  { id: 'explore-quotes', label: 'Explore quotes' },
  { id: 'ccrc-offer', label: 'Evaluate a CCRC offer' },
  { id: 'comprehensive-plan', label: 'Build a comprehensive financial plan for LTC' },
  { id: 'something-else', label: 'Something else' },
] as const;

export type FreemiumIntentId = (typeof FREEMIUM_INTENT_OPTIONS)[number]['id'];

// WAG handoff recipient for licensed-tier selections.
export const WAG_SUPPORT_EMAIL = 'support@wealthassurancegroup.com';
